/* eslint-disable tsdoc/syntax */
import { ButtonComponent, NestedMenuItemComponent } from "@zenith/components";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  TRANSACTIONS_DATE_FORMAT,
  TRANSACTIONS_PAGE_SIZE,
} from "@zenith/data/constants";
import { useTablePagination } from "@zenith/hooks";
import {
  OperationsApiService,
  SalesItemDTO,
  SalesSummaryRecord,
} from "@zenith/services/transactions";
import { OutletApiService } from "@zenith/services/user";
import { useNotificationContext, useUserDetailsContext } from "@zenith/context";
import { usePermissions } from "@zenith/features/AuthZ/usePermissions";
import { ANALYTICS_UNDO_SALE } from "@zenith/routes/paths";
import { ToastSnackbarComponent } from "@zenith/components";
import Check from "@zenith/assets/icons/Check";
import { ExportIcon } from "@zenith/assets/icons/components";
import { downloadFileFromBlob } from "@zenith/utils/Helpers/downloadFileUtil";
import { TransactionsParametersExportRecord } from "@zenith/features/Transactions/interfaces";
import UndoSaleModal from "../../UndoSaleModal/undo-sale-modal";
import type {
  SalesHistoryReturnState,
  UndoSalePinPageState,
} from "@zenith/pages/AnalyticsPage/UndoSalePinPage/undo-sale-pin-page";
import {
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useMediaQuery,
} from "@mui/material";
import dayjs from "dayjs";
import { formatCurrency } from "@zenith/utils";
import ChevronLeft from "@zenith/assets/icons/ChevronLeft";
import ChevronRight from "@zenith/assets/icons/ChevronRight";
import {
  AmountTransactionFilter,
  DateTransactionsFilter,
} from "@zenith/features/Transactions/components";
import { EmptyState } from "@zenith/components/ui/EmptyStateComponent/empty-state-component/empty-state-component";
import { NoSales } from "@zenith/assets/illustrations/NoSales";
import { SearchComponent } from "@zenith/components/ui/SearchComponent/search-component";
import ServerErrorHandler from "@zenith/features/AppStateHandlers/ServerErrorHandler/server-error-handler";
import { removeUnderscores } from "@zenith/utils/Formaters/removeUnderscore";

const AMOUNT_PER_UNIT_LABEL = "Amount Per Unit";

const SALE_PAYMENT_METHOD_LABELS: Record<string, string> = {
  CASH: "Cash",
  STATIC_ACCOUNT: "Static Account",
  PAY_WITH_TRANSFER: "Pay With Transfer",
  INWARDS_TRANSFER: "Transfer",
  IN_BANK_TRANSFER: "Transfer",
  QR_CODE: "QR Code",
  USSD: "USSD",
  POS: "POS",
  POS_ORDER: "POS",
  CARD: "Card",
  PAYMENT_LINK: "Payment Link",
  PAYMENT_LINK_CARD: "Payment Link",
  PAYMENT_LINK_USSD: "Payment Link",
  PAYMENT_LINK_QR_CODE: "Payment Link",
  PAYMENT_LINK_PAY_WITH_TRANSFER: "Payment Link",
};

const SALE_PAYMENT_METHOD_FILTERS: {
  value: string;
  label: string;
  paymentMethods: string[];
}[] = [
  { value: "CASH", label: "Cash", paymentMethods: ["CASH"] },
  {
    value: "STATIC_ACCOUNT",
    label: "Static Account",
    paymentMethods: ["STATIC_ACCOUNT"],
  },
  {
    value: "PAY_WITH_TRANSFER",
    label: "Pay With Transfer",
    paymentMethods: ["PAY_WITH_TRANSFER"],
  },
  { value: "USSD", label: "USSD", paymentMethods: ["USSD"] },
  { value: "QR_CODE", label: "QR Code", paymentMethods: ["QR_CODE"] },
  {
    value: "PAYMENT_LINK",
    label: "Payment Link",
    paymentMethods: [
      "PAYMENT_LINK",
      "PAYMENT_LINK_CARD",
      "PAYMENT_LINK_USSD",
      "PAYMENT_LINK_QR_CODE",
      "PAYMENT_LINK_PAY_WITH_TRANSFER",
    ],
  },
];

const toPaymentMethodsParam = (selected: string[]) =>
  SALE_PAYMENT_METHOD_FILTERS.filter((option) =>
    selected.includes(option.value)
  )
    .flatMap((option) => option.paymentMethods)
    .join(",");

/** Only completed cash and static account sales can be undone. */
const UNDOABLE_PAYMENT_METHODS = ["CASH", "STATIC_ACCOUNT"];

interface Collector {
  id: string;
  name: string;
}

const salePaymentMethodLabel = (paymentMethod?: string) =>
  paymentMethod
    ? SALE_PAYMENT_METHOD_LABELS[paymentMethod] ??
      removeUnderscores(paymentMethod)
    : "-";

/* eslint-disable-next-line */
export interface SalesHistoryListComponentProps {}

interface FiltersData {
  maximumAmount: number | undefined;
  minimumAmount: number | undefined;
  transactionDateEnd: string | undefined;
  transactionDateStart: string | undefined;
  paymentMethods: string[];
  initiatorIds: string[];
}

const SalesHistoryListComponent = memo(function SalesHistoryListComponent(
  props: SalesHistoryListComponentProps
) {
  const initialFilters: FiltersData = useMemo(
    () => ({
      maximumAmount: undefined,
      minimumAmount: undefined,
      transactionDateEnd: undefined,
      transactionDateStart: undefined,
      paymentMethods: [],
      initiatorIds: [],
    }),
    []
  );

  interface DatesFilter {
    startDate: unknown;
    endDate: unknown;
  }

  const [isEmptySearch, setIsEmptySearch] = useState(false);
  const [searchText, setsearchText] = useState<string>("");
  const [searchTextToSend, setSearchTextToSend] = useState<string>("");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [totalNoOfItems, setTotalNoOfItems] = useState(0);
  const [isNewRequest, setIsNewRequest] = useState<boolean>(true);
  const [isLoadingSalesItems, setIsLoadingSalesItems] = useState<boolean>(true);
  const [operations, setOperations] = useState<SalesItemDTO[]>();
  const [filters, setFilters] = useState<FiltersData>(initialFilters);
  const [dates, setDates] = useState<DatesFilter>({
    startDate: undefined,
    endDate: undefined,
  });
  const [serverError, setServerError] = useState<boolean>(false);
  const [summary, setSummary] = useState<SalesSummaryRecord>();
  const [collectors, setCollectors] = useState<Collector[]>([]);
  const [saleToUndo, setSaleToUndo] = useState<SalesItemDTO | null>(null);
  const [exportAnchorEl, setExportAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [toasterData, setToasterData] = useState<{
    title: string;
    message: string;
    status: "success" | "error" | "warning";
  }>({ title: "", message: "", status: "success" });

  const navigate = useNavigate();
  const location = useLocation();
  const { setOpen } = useNotificationContext();
  const { userDetailsContext } = useUserDetailsContext();
  const { id: merchantId, walletId, firstName, lastName } = userDetailsContext;
  // BOAS-7641: the Analytics table gives Manager "Undo Sale" alongside Admin,
  // and every role may export the sales it can see. The claim on the token is
  // the profile used at sign-in, so the role in use is read from the profile.
  const { role: profileRole, can } = usePermissions();
  const isAdmin = profileRole === "admin";
  const canUndoSale = can("sale:undo");
  const canExportSales = can("analytics:view");

  const notify = (
    title: string,
    message: string,
    status: "success" | "error" | "warning"
  ) => {
    setToasterData({ title, message, status });
    setOpen(true);
  };

  // Back from the PIN page after undoing a sale.
  useEffect(() => {
    const returnState = location.state as SalesHistoryReturnState | null;
    if (returnState?.undoneSale) {
      notify(
        "Sale undone",
        "The stock has been added back to your inventory.",
        "success"
      );
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "Collected by" lists the merchant and the employees of the outlet in their
  // token (matched by its wallet), the sales the list covers.
  useEffect(() => {
    if (!isAdmin) return;
    const merchant: Collector = {
      id: merchantId,
      name: [firstName, lastName].filter(Boolean).join(" ") || "Me",
    };
    setCollectors([merchant]);

    OutletApiService.listOutlets()
      .then((response) => {
        const outlets = response.data || [];
        const outlet =
          outlets.find((o) => String(o.walletId) === String(walletId)) ||
          outlets.find((o) => o.isDefault) ||
          outlets[0];
        return outlet?.id
          ? OutletApiService.getOutletEmployees(outlet.id)
          : undefined;
      })
      .then((response) => {
        const employees = (response?.data || [])
          .filter((employee) => employee.id && employee.id !== merchantId)
          .map((employee) => ({
            id: employee.id as string,
            name: employee.name || "-",
          }));
        setCollectors([merchant, ...employees]);
      })
      .catch((error) => {
        console.error("Failed to load the outlet's employees:", error);
      });
  }, [isAdmin, merchantId, walletId, firstName, lastName]);

  const open = Boolean(anchorEl);

  const { currentPage, goToPage, maxPage, next, prev } = useTablePagination(
    TRANSACTIONS_PAGE_SIZE,
    totalNoOfItems
  );

  function getRequestObject() {
    const {
      maximumAmount,
      minimumAmount,
      transactionDateEnd,
      transactionDateStart,
    } = filters || undefined;

    const page = currentPage <= 0 ? 0 : currentPage - 1;

    return [
      page.toString(),
      TRANSACTIONS_PAGE_SIZE.toString(),
      transactionDateStart,
      transactionDateEnd,
      filters.paymentMethods.length
        ? toPaymentMethodsParam(filters.paymentMethods)
        : undefined,
      minimumAmount?.toString(),
      maximumAmount?.toString(),
      searchTextToSend || undefined,
      undefined,
      filters.initiatorIds.length ? filters.initiatorIds.join(",") : undefined,
    ];
  }

  const latestRequestRef = useRef(0);

  useEffect(() => {
    if (!isNewRequest) return;
    setIsNewRequest(false);
    const transactionsRequest = getRequestObject();
    const requestId = ++latestRequestRef.current;
    const isLatest = () => requestId === latestRequestRef.current;

    setIsLoadingSalesItems(true);
    setServerError(false);
    OperationsApiService.getSalesItemsSummary(...transactionsRequest)
      .then((response) => isLatest() && setSummary(response.data))
      .catch(() => isLatest() && setSummary(undefined));
    OperationsApiService.getSalesItems(...transactionsRequest)
      .then((response) => {
        if (!isLatest()) return;
        setIsEmptySearch(false);
        setOperations(response.data?.content || []);
        setTotalNoOfItems(response.data?.totalItems || 0);
      })
      .catch((error) => {
        if (!isLatest()) return;
        if (error.status === 404) {
          setOperations([]);
          setTotalNoOfItems(0);
          (searchText || filters !== initialFilters) && setIsEmptySearch(true);
        } else {
          setServerError(true);
        }
      })
      .finally(() => isLatest() && setIsLoadingSalesItems(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewRequest]);

  useEffect(() => {
    if (!searchText && isEmptySearch && filters === initialFilters) {
      setIsEmptySearch(false);
    }
  }, [filters, initialFilters, isEmptySearch, searchText]);

  useEffect(() => {
    if (searchText === "" && searchTextToSend !== "") {
      setSearchTextToSend("");
      setIsNewRequest(true);
    }
  }, [searchText, searchTextToSend]);

  const handleSearchEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      setSearchTextToSend(searchText);
      setIsNewRequest(true);
      handlePageChange(1);
    } else {
      event.stopPropagation();
    }
  };

  const handleSearchClick = () => {
    setSearchTextToSend(searchText);
    setIsNewRequest(true);
    handlePageChange(1);
  };

  function filtersLength(): number {
    let length = 0;

    if (filters.maximumAmount || filters.minimumAmount) length++;

    if (filters.transactionDateStart || filters.transactionDateEnd) length++;

    if (filters.paymentMethods.length) length++;

    if (filters.initiatorIds.length) length++;

    return length;
  }

  const toggleFilterValue = (
    key: "paymentMethods" | "initiatorIds",
    value: string
  ) => {
    setFilters((current) => ({
      ...current,
      [key]: current[key].includes(value)
        ? current[key].filter((item) => item !== value)
        : [...current[key], value],
    }));
  };

  const canUndo = (sale: SalesItemDTO) =>
    canUndoSale &&
    Boolean(sale.operationId) &&
    (sale.status ?? "SUCCESS") === "SUCCESS" &&
    UNDOABLE_PAYMENT_METHODS.includes(sale.paymentMethod ?? "");

  const handleConfirmUndo = () => {
    if (!saleToUndo?.operationId) return;
    const state: UndoSalePinPageState = {
      operationId: saleToUndo.operationId,
    };
    setSaleToUndo(null);
    navigate(ANALYTICS_UNDO_SALE, { state });
  };

  // Exports completed sales through the operations export, with the list's
  // date and payment method filters (all sale payment methods by default).
  const handleExport = (
    mediaType: TransactionsParametersExportRecord.mediaType
  ) => {
    setExportAnchorEl(null);
    setIsExporting(true);
    const paymentMethods = toPaymentMethodsParam(
      filters.paymentMethods.length
        ? filters.paymentMethods
        : SALE_PAYMENT_METHOD_FILTERS.map((option) => option.value)
    );

    OperationsApiService.exportOperations(
      mediaType,
      undefined,
      undefined,
      "DESC",
      "OPERATION_DATE",
      filters.transactionDateStart,
      filters.transactionDateEnd,
      paymentMethods,
      undefined,
      undefined,
      "SUCCESS"
    )
      .then((response) =>
        downloadFileFromBlob(
          response,
          `sales-${new Date().getTime()}`,
          mediaType
        )
      )
      .catch((error) =>
        notify(
          "Export failed",
          error?.body?.message || "We could not export your sales.",
          "error"
        )
      )
      .finally(() => setIsExporting(false));
  };

  const filterCount = filtersLength();

  function handleMenuClose(): void {
    return setAnchorEl(null);
  }

  function clearFilters(): void {
    setDates({ startDate: undefined, endDate: undefined });
    setFilters(initialFilters);
    setIsNewRequest(true);
  }

  function handleClearAllButtonClick(
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): void {
    clearFilters();
    handleMenuClose();
  }

  function handleApplyButtonClick(
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): void {
    handlePageChange(1);
    setIsNewRequest(true);
    handleMenuClose();
  }

  const handleFiltersButtonClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    if (!open) {
      setAnchorEl(e.currentTarget);
    } else {
      setAnchorEl(null);
    }
  };

  function clearAmountFilter(): void {
    setFilters({
      ...filters,
      minimumAmount: undefined,
      maximumAmount: undefined,
    });
    setIsNewRequest(true);
  }

  function applyAmountFilter(
    minimumAmount: number | undefined,
    maximumAmount: number | undefined
  ): void {
    setFilters({
      ...filters,
      minimumAmount,
      maximumAmount,
    });
  }

  function clearDateFilter(): void {
    setDates({ startDate: undefined, endDate: undefined });
    setFilters({
      ...filters,
      transactionDateStart: undefined,
      transactionDateEnd: undefined,
    });
    setIsNewRequest(true);
  }

  function applyDateFilter(
    transactionDateStart: unknown | undefined,
    transactionDateEnd: unknown | undefined
  ): void {
    setDates({ startDate: transactionDateStart, endDate: transactionDateEnd });
    formatDateToFilters(transactionDateStart, transactionDateEnd);
  }

  // FIXED: This function now properly handles timezone by using dayjs
  function formatDateToFilters(
    transactionDateStart: unknown,
    transactionDateEnd: unknown
  ) {
    let startDate: string | undefined = undefined;
    let endDate: string | undefined = undefined;

    if (
      transactionDateStart &&
      typeof transactionDateStart === "object" &&
      "$d" in transactionDateStart
    ) {
      // Use dayjs to handle local timezone properly without conversion
      startDate = dayjs(transactionDateStart.$d as string).format("YYYY-MM-DD");
    }

    if (
      transactionDateEnd &&
      typeof transactionDateEnd === "object" &&
      "$d" in transactionDateEnd
    ) {
      // Use dayjs to handle local timezone properly without conversion
      endDate = dayjs(transactionDateEnd.$d as string).format("YYYY-MM-DD");
    }

    setFilters({
      ...filters,
      transactionDateStart: startDate,
      transactionDateEnd: endDate,
    });
  }

  function handlePrevPage(): void {
    setIsLoadingSalesItems(true);
    setIsNewRequest(true);
    prev();
  }

  function handleNextPage(): void {
    setIsLoadingSalesItems(true);
    setIsNewRequest(true);
    next();
  }

  function handlePageChange(p: number): void {
    setIsLoadingSalesItems(true);
    setIsNewRequest(true);
    goToPage(p);
  }

  const nestedMenuItemClassNames =
    "ml-2.5 py-4 px-2 w-52 hover:rounded-lg hover:bg-neutral-25 text-zinc-600";

  const isSmallScreen = useMediaQuery("(max-width: 640px)");

  return (
    <>
      <ToastSnackbarComponent
        horizontal="center"
        status={toasterData.status}
        vertical="top"
        hasAutoHideDuration
        size="lg"
        title={toasterData.title}
        text={toasterData.message}
      />
      <UndoSaleModal
        isOpen={Boolean(saleToUndo)}
        isPaidIntoAccount={saleToUndo?.paymentMethod === "STATIC_ACCOUNT"}
        onUndo={handleConfirmUndo}
        onClose={() => setSaleToUndo(null)}
      />

      <div className="mt-8 flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-wrap gap-6">
          <div id="sales-summary-total-value">
            <p className="text-xs text-neutral-500">
              Total Value of completed sales
            </p>
            <p className="mt-1 text-3xl font-medium text-neutral-900">
              {formatCurrency(summary?.totalValue ?? 0)}
            </p>
          </div>
          <div className="hidden border-l border-neutral-200 md:block" />
          <div id="sales-summary-completed-sales">
            <p className="text-xs text-neutral-500">
              Total Number of completed sales
            </p>
            <p className="mt-1 text-3xl font-medium text-neutral-900">
              {(summary?.completedSales ?? 0).toLocaleString()}
            </p>
          </div>
        </div>
        {canExportSales && (
          <>
            <ButtonComponent
              label="Export"
              id="export-sales-button"
              size="large"
              type="secondary"
              disabled={!totalNoOfItems || isExporting}
              isLoading={isExporting}
              iconStart={<ExportIcon fill={"currentColor"} />}
              onClick={(e) => setExportAnchorEl(e.currentTarget)}
            />
            <Menu
              anchorEl={exportAnchorEl}
              open={Boolean(exportAnchorEl)}
              onClose={() => setExportAnchorEl(null)}
            >
              <MenuItem
                id="export-sales-csv"
                onClick={() =>
                  handleExport(TransactionsParametersExportRecord.mediaType.CSV)
                }
              >
                Export CSV
              </MenuItem>
              <MenuItem
                id="export-sales-pdf"
                onClick={() =>
                  handleExport(TransactionsParametersExportRecord.mediaType.PDF)
                }
              >
                Export PDF
              </MenuItem>
            </Menu>
          </>
        )}
      </div>

      <div className="mt-8 block flex-wrap items-center md:flex">
        <div className="mr-4 w-[325px]">
          <SearchComponent
            size="medium"
            placeholder="Search by item"
            value={searchText}
            onChange={setsearchText}
            isAutocomplete={false}
            onSearch={handleSearchClick}
            onKeyDown={handleSearchEnter}
          />
        </div>
        <button
          className={`inline-flex h-10 items-center justify-center gap-2 rounded-lg px-4 py-2 text-center text-sm font-normal capitalize leading-tight text-zinc-600 ${
            !open
              ? `max-w-103 border border-stone-300 hover:border-neutral-300`
              : `w-108 z-1350 bg-zinc-100 hover:border-neutral-300`
          }`}
          id="filters-button"
          onClick={handleFiltersButtonClick}
        >
          {!open ? "Filters" : "Close Filters"}{" "}
          {!open && filterCount > 0 && (
            <Chip
              className="inline-flex h-6 w-6 flex-col items-center justify-center gap-2.5 rounded-3xl bg-neutral-400 px-1.5 text-center text-xs font-medium text-white"
              label={filterCount}
              size="small"
            />
          )}
        </button>

        {!open && filterCount > 0 && (
          <ButtonComponent
            label="Clear all filters"
            onClick={handleClearAllButtonClick}
            id="clear-all-button"
            size="large"
            type="tertiary"
            // fullWidth
          />
        )}

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={() => setAnchorEl(null)}
          sx={{
            ".MuiPaper-root.MuiPaper-elevation.MuiPaper-rounded.MuiPopover-paper.MuiMenu-paper.MuiMenu-paper":
              {
                display: "flex",
                mt: 1,
                width: "229px",
                flexDirection: "column",
                alignItems: "flex-start",
              },
          }}
        >
          <NestedMenuItemComponent
            label="Payment Method"
            parentMenuOpen={open}
            className={nestedMenuItemClassNames}
            sx={{
              ".MuiTypography-root": {
                fontSize: "14px",
              },
            }}
            filterCount={filters.paymentMethods.length ? 1 : 0}
          >
            {SALE_PAYMENT_METHOD_FILTERS.map((method) => (
              <MenuItem
                className="mx-2.5 h-10 w-64 items-center justify-between gap-3 rounded-lg py-2 pl-3 pr-4 text-sm font-normal leading-none text-zinc-600 hover:bg-stone-50"
                id={`${method.value}-sales-filter-button`}
                onClick={() =>
                  toggleFilterValue("paymentMethods", method.value)
                }
                key={method.value}
              >
                {method.label}
                {filters.paymentMethods.includes(method.value) && <Check />}
              </MenuItem>
            ))}
          </NestedMenuItemComponent>

          {isAdmin && (
            <NestedMenuItemComponent
              label="Collected by"
              parentMenuOpen={open}
              className={nestedMenuItemClassNames}
              sx={{
                ".MuiTypography-root": {
                  fontSize: "14px",
                },
              }}
              filterCount={filters.initiatorIds.length ? 1 : 0}
            >
              {collectors.map((collector) => (
                <MenuItem
                  className="mx-2.5 h-10 w-64 items-center justify-between gap-3 rounded-lg py-2 pl-3 pr-4 text-sm font-normal capitalize leading-none text-zinc-600 hover:bg-stone-50"
                  id={`${collector.id}-collector-filter-button`}
                  onClick={() =>
                    toggleFilterValue("initiatorIds", collector.id)
                  }
                  key={collector.id}
                >
                  {collector.name}
                  {filters.initiatorIds.includes(collector.id) && <Check />}
                </MenuItem>
              ))}
            </NestedMenuItemComponent>
          )}

          <NestedMenuItemComponent
            label="Date"
            parentMenuOpen={open}
            className={nestedMenuItemClassNames}
            sx={{
              ".MuiTypography-root": {
                fontSize: "14px",
              },
            }}
            filterCount={
              filters.transactionDateStart || filters.transactionDateEnd ? 1 : 0
            }
          >
            <DateTransactionsFilter
              minDate={dates.startDate}
              maxDate={dates.endDate}
              clear={clearDateFilter}
              apply={applyDateFilter}
              requireStartDate
            />
          </NestedMenuItemComponent>

          <NestedMenuItemComponent
            label={AMOUNT_PER_UNIT_LABEL}
            parentMenuOpen={open}
            className={nestedMenuItemClassNames}
            sx={{
              ".MuiTypography-root": {
                fontSize: "14px",
              },
            }}
            filterCount={filters.maximumAmount || filters.minimumAmount ? 1 : 0}
          >
            {/* TODO: TO BE ADDED IN THE FUTURE */}
            <AmountTransactionFilter
              maximumValue={filters.maximumAmount}
              minimumValue={filters.minimumAmount}
              clear={clearAmountFilter}
              apply={applyAmountFilter}
            />
          </NestedMenuItemComponent>

          <MenuItem className="mt-3 flex cursor-default flex-wrap justify-between hover:bg-white">
            <button
              className="inline-flex h-9 w-[94.5px] items-center justify-center gap-2 rounded-lg px-8 py-2 text-center text-sm capitalize text-zinc-600 hover:text-zinc-900 hover:underline disabled:text-gray-300 disabled:no-underline"
              onClick={handleClearAllButtonClick}
              disabled={!filterCount}
            >
              Clear all
            </button>

            <button
              className="inline-flex h-9 w-[94.5px] items-center justify-center gap-2 rounded-lg bg-red-600 px-8 py-2 text-center text-sm text-white disabled:bg-zinc-100 disabled:text-gray-300"
              onClick={handleApplyButtonClick}
              disabled={!filterCount}
            >
              <span>Apply {filterCount > 0 ? `(${filterCount})` : ""}</span>
            </button>
          </MenuItem>
        </Menu>

        <p className="my-4 text-sm text-neutral-600 md:my-0 md:ml-auto">
          {`Showing ${(!isLoadingSalesItems && operations?.length) || 0} of ${
            (!isLoadingSalesItems && totalNoOfItems) || 0
          } Items`}
        </p>
      </div>

      <div className="mt-6 h-[calc(100vh-460px)] overflow-auto border-t border-neutral-200">
        <Table stickyHeader>
          <TableHead>
            {(totalNoOfItems as number) > 0 && (
              <TableRow className="[&>th]:border-b-0 [&>th]:pt-6">
                <TableCell className="min-w-[110px] items-center text-sm text-neutral-400">
                  Date
                </TableCell>
                <TableCell className="min-w-[160px] text-left text-sm text-neutral-400">
                  Collected By
                </TableCell>
                <TableCell className="min-w-[160px] text-left text-sm text-neutral-400">
                  Item Description
                </TableCell>
                <TableCell className="min-w-[130px] text-left text-sm text-neutral-400">
                  Payment Method
                </TableCell>
                <TableCell className="min-w-[90px] text-left text-sm text-neutral-400">
                  Quantity
                </TableCell>
                <TableCell className="min-w-[150px] text-sm text-neutral-400">
                  {AMOUNT_PER_UNIT_LABEL}
                </TableCell>
                {canUndoSale && (
                  <TableCell className="min-w-[140px] text-right text-sm text-neutral-400">
                    Action
                  </TableCell>
                )}
              </TableRow>
            )}
          </TableHead>

          <TableBody>
            {isLoadingSalesItems ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="h-[calc(100vh-400px)] w-full border-b-0"
                >
                  <div className="flex justify-center text-center">
                    <CircularProgress
                      className={"text-[#F3B8B3]"}
                      size={"3rem"}
                    />
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              operations?.map((operation: SalesItemDTO, idx) => (
                <TableRow
                  key={`${operation.amountPerUnit}-${operation.createdAt}-${idx}`}
                  className="h-16 hover:bg-stone-50 [&>td]:py-2"
                >
                  <TableCell className="min-w-[110px] text-neutral-400">
                    {dayjs(operation.createdAt).format(
                      TRANSACTIONS_DATE_FORMAT
                    )}
                  </TableCell>
                  <TableCell className="min-w-[160px] capitalize text-neutral-600">
                    {operation.initiatorName || "-"}
                  </TableCell>
                  <TableCell className="min-w-[160px] capitalize text-neutral-600">
                    {operation.description}
                  </TableCell>
                  <TableCell className="min-w-[130px] text-neutral-600">
                    {salePaymentMethodLabel(operation.paymentMethod)}
                  </TableCell>
                  <TableCell className="min-w-[90px] uppercase text-neutral-600">
                    {operation.quantity}
                  </TableCell>
                  <TableCell className="min-w-[150px] uppercase text-neutral-600">
                    <p>{formatCurrency(operation.amountPerUnit, true)}</p>
                  </TableCell>
                  {canUndoSale && (
                    <TableCell className="min-w-[140px] text-right">
                      {canUndo(operation) && (
                        <button
                          type="button"
                          id={`undo-sale-${operation.operationId}-${idx}`}
                          className="inline-flex items-center gap-1 text-sm text-neutral-700 hover:text-primary-300"
                          onClick={() => setSaleToUndo(operation)}
                        >
                          <ReplayIcon sx={{ fontSize: 16 }} />
                          Undo Sales
                        </button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {serverError ? (
          <ServerErrorHandler
            errorTitle="We are unable to fetch analytics"
            errorMessage="Please try again. Apologies for the inconvenience."
          />
        ) : (
          !isLoadingSalesItems &&
          totalNoOfItems === 0 && (
            <div className="flex h-full w-full flex-row items-center justify-center pt-16">
              <EmptyState
                title="No Sales Found"
                description={
                  isEmptySearch
                    ? "No matching items found."
                    : "It seems there are no sales to display at the moment."
                }
                sub={
                  isEmptySearch
                    ? "Try a different search or filter."
                    : undefined
                }
                illustration={
                  <div className="flex w-full justify-center">
                    <NoSales className="block h-auto w-20 text-primary-300" />
                  </div>
                }
              />
            </div>
          )
        )}
      </div>

      <div className="mt-2 inline-flex h-8 w-full items-center justify-end">
        <div className="flex flex-row">
          <Pagination
            count={maxPage}
            page={currentPage}
            variant="text"
            shape="rounded"
            siblingCount={isSmallScreen ? 0 : 1}
            onChange={(e, p) => handlePageChange(p)}
            hidePrevButton
            hideNextButton
            sx={{
              ".MuiPaginationItem-root.Mui-selected": {
                backgroundColor: "#DA291C",
                color: "white",
              },
              ".MuiPaginationItem-root:hover": {
                backgroundColor: "#DA291C",
                color: "white",
              },
              ".MuiPaginationItem-root.Mui-selected:hover": {
                cursor: "default",
                backgroundColor: "#DA291C",
              },
            }}
          />
          {maxPage > 1 && (
            <>
              <button
                disabled={currentPage === 1}
                className={
                  "flex h-8 w-8 items-center justify-center rounded-md text-center text-sm text-zinc-600 hover:bg-primary-200 hover:text-white disabled:text-neutral-400 disabled:hover:bg-transparent"
                }
              >
                <ChevronLeft
                  onClick={handlePrevPage}
                  color={currentPage === 1 ? "text-zinc-900" : "black"}
                />
              </button>
              <button
                disabled={currentPage === maxPage}
                className="flex h-8 w-8 items-center justify-center rounded-md text-center text-sm text-zinc-600 hover:bg-primary-200 hover:text-white disabled:text-neutral-400 disabled:hover:bg-transparent"
              >
                <ChevronRight
                  onClick={handleNextPage}
                  color={currentPage === maxPage ? "text-zinc-900" : "black"}
                />
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
});

export default SalesHistoryListComponent;
